import { useMemo, useState } from "react";
import { EditIcon } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { ChatStream, UpdateChatStreamValidation, useUpdateChatStream } from "@/lib/api/chats";
import { ChatStreamForm, ChatStreamFormData } from "./ChatStreamForm";
import { getUniqueStrings } from "@/lib/utils";

interface BaseUpdateParticipantInfo {
  update_type: "add" | "remove";
  customer_id?: string;
  sms_number?: string;
}

export interface UpdateChatStreamProps {
  chatStream: ChatStream;
  onSuccess?: () => void;
  onClose?: () => void;
}

export function UpdateChatStream({
  chatStream,
  onSuccess,
  onClose: externalOnClose
}: UpdateChatStreamProps) {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const updateChatStream = useUpdateChatStream();

  const {
    originalCustomerIds,
    originalPhoneNumbers,
    originalName,
  } = useMemo(() => {
    const originalCustomerIds = getUniqueStrings(chatStream.Participants?.filter((participant) => participant.customer_id).map((participant) => participant.customer_id!) || []);
    const originalPhoneNumbers = getUniqueStrings(chatStream.Participants?.filter((participant) => !!participant.sms_number && !participant.customer_id).map((participant) => participant.sms_number!) || []);
    const originalName = chatStream.name;
    return {
      originalCustomerIds,
      originalPhoneNumbers,
      originalName,
    };
  }, [chatStream]);

  if (!chatStream) return null;

  const onClose = () => {
    if (externalOnClose) {
      externalOnClose();
    }
  };

  const onStatusChange = (status: boolean) => {
    if (externalOnClose && !status) {
      externalOnClose();
    }
  };

  const handleSubmit = async (formData: ChatStreamFormData) => {
    if (isUpdating) return;
    setIsUpdating(true);

    try {
      // Extract current participants from form data
      const currentCustomerIds = formData.participants?.filter(p => p.customer_id).map(p => p.customer_id) || [];
      const currentPhoneNumbers = formData.participants?.filter(p => p.sms_number && !p.customer_id).map(p => p.sms_number) || [];

      // Determine changes
      const participants: BaseUpdateParticipantInfo[] = [];

      // Find added customers
      const addedCustomers = currentCustomerIds.filter(id => !originalCustomerIds.includes(id));
      addedCustomers.forEach(customer_id => {
        participants.push({ update_type: "add" as const, customer_id });
      });

      // Find removed customers
      const removedCustomers = originalCustomerIds.filter(id => !currentCustomerIds.includes(id));
      removedCustomers.forEach(customer_id => {
        participants.push({ update_type: "remove" as const, customer_id });
      });

      // Find added phone numbers
      const addedPhoneNumbers = currentPhoneNumbers.filter(number => !originalPhoneNumbers.includes(number));
      addedPhoneNumbers.forEach(sms_number => {
        participants.push({ update_type: "add" as const, sms_number });
      });

      // Find removed phone numbers
      const removedPhoneNumbers = originalPhoneNumbers.filter(number => !currentPhoneNumbers.includes(number));
      removedPhoneNumbers.forEach(sms_number => {
        participants.push({ update_type: "remove" as const, sms_number });
      });

      // Check if there are any changes to make
      const hasNameChange = formData.name !== originalName;
      const hasParticipantChanges = participants.length > 0;

      if (!hasNameChange && !hasParticipantChanges) {
        onClose();
        return;
      }

      // Build update payload
      const updatePayload = {
        chat_stream_id: chatStream.id,
        name: formData.name,
        participants: hasParticipantChanges ? participants : undefined,
      };

      // Validate the update payload
      const dataResult = UpdateChatStreamValidation.safeParse(updatePayload);
      if (!dataResult.success) {
        toast({
          title: "Invalid data",
          description: dataResult.error.issues[0]?.message || 'Validation failed',
        });
        return;
      }

      // Call the update API
      const result = await updateChatStream(dataResult.data);

      if (result.success) {
        toast({
          title: "Chat stream updated",
          description: `Successfully updated "${formData.name}"`,
        });
        onClose();
        onSuccess?.();
      } else {
        toast({
          title: "Failed to update chat stream",
          description: result.message || "",
        });
      }
    } catch (error) {
      console.error("Failed to update chat stream", error);
      toast({
        title: "Failed to update chat stream",
        description: "",
      });
    } finally {
      setIsUpdating(false);
    }
  };


  return (
    <Dialog open={true} onOpenChange={onStatusChange}>
      <DialogContent className="sm:max-w-[900px] lg:max-w-[1200px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Update Chat Stream</DialogTitle>
          <DialogDescription>
            Update the group chat stream configuration
          </DialogDescription>
        </DialogHeader>

        <ChatStreamForm
          mode="update"
          defaultValues={{
            name: chatStream.name,
            customerIds: originalCustomerIds,
            phoneNumbers: originalPhoneNumbers,
            chatStream
          }}
          onSubmit={handleSubmit}
          isSubmitting={isUpdating}
        >
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isUpdating}>
              Cancel
            </Button>
          </DialogFooter>
        </ChatStreamForm>
      </DialogContent>
    </Dialog>
  );
}
